import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { formatEventDate, getDisplayStatus } from '../utils/date-utils';
import { parseCityState, STATUS_COLORS } from './EventHoverCard';
import useIsMobile from '../components/ChatBot/useIsMobile';
import { DOCK_MOTION } from '../components/ChatBot/flerpieDock';
import TransportChips from '../components/TransportChips';
import { IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { IconClose } from '../components/ui/icons';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';

const POPUP_W = 420;
const PAD = 8;
const GAP = 6;
const FALLBACK_H = 360;

type AnyEvent = Record<string, any>;

export type EventPopupState = {
  event: AnyEvent;
  clickX: number;
  clickY: number;
  triggerEl: HTMLElement | null;
};

type Props = { popup: EventPopupState; onClose: () => void };

// ---------------------------------------------------------------------------
// Status accent — the colored strip down the left edge
// ---------------------------------------------------------------------------
const STATUS_ACCENT: Record<string, string> = {
  pending:   'bg-amber-400 day:bg-amber-500',
  confirmed: 'bg-emerald-400 day:bg-emerald-500',
  packing:   'bg-purple-400 day:bg-purple-500',
  active:    'bg-sky-400 day:bg-sky-500',
  completed: 'bg-outline day:bg-gray-400',
  cancelled: 'bg-red-500 day:bg-red-600',
  rental:    'bg-orange-400 day:bg-orange-500',
  lease:     'bg-blue-400 day:bg-blue-500',
};

const accentFor = (event: AnyEvent): string => {
  if (event._isLease) return STATUS_ACCENT.lease;
  if (event._isRental) return STATUS_ACCENT.rental;
  return STATUS_ACCENT[getDisplayStatus(event)] || STATUS_ACCENT.pending;
};

// ---------------------------------------------------------------------------
// Icons — small, consistent, currentColor stroke
// ---------------------------------------------------------------------------
const Icon: React.FC<{ d: string; className?: string }> = ({ d, className = '' }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    className={`w-[18px] h-[18px] flex-shrink-0 ${className}`}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}
    strokeLinecap="round" strokeLinejoin="round"
  >
    <path d={d} />
  </svg>
);

// Heroicons-style outline paths (single path each — copy/paste safe)
const ICONS = {
  clock:    'M12 8v4l3 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  pin:      'M12 21s-7-7.5-7-12a7 7 0 1114 0c0 4.5-7 12-7 12zM12 11.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
  tasklist: 'M9 5h10M9 12h10M9 19h10M5 5h.01M5 12h.01M5 19h.01',
  music:    'M9 18V6l11-2v12M9 18a3 3 0 11-6 0 3 3 0 016 0zm11-2a3 3 0 11-6 0 3 3 0 016 0z',
  truck:    'M3 7h11v8H3zM14 10h4l3 3v2h-7zM7 17a2 2 0 100-4 2 2 0 000 4zm10 0a2 2 0 100-4 2 2 0 000 4z',
  users:    'M16 17v-1a4 4 0 00-4-4H6a4 4 0 00-4 4v1m13-7a4 4 0 100-8 4 4 0 000 8zm6 7v-1a4 4 0 00-3-3.87M14 3.13a4 4 0 010 7.75M9 10a4 4 0 100-8 4 4 0 000 8z',
  phone:    'M22 16.92v3a2 2 0 01-2.18 2 19.8 19.8 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.8 19.8 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.33 1.85.57 2.81.7A2 2 0 0122 16.92z',
  notes:    'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M9 13h6M9 17h6M9 9h2',
  link:     'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71',
  ticket:   'M2 9V5a2 2 0 012-2h16a2 2 0 012 2v4a2 2 0 000 4v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4a2 2 0 000-4zM13 5v2M13 11v2M13 17v2',
  building: 'M3 21h18M5 21V7l8-4v18M19 21V11l-6-3M9 9h.01M9 13h.01M9 17h.01',
  arrow:    'M5 12h14M13 5l7 7-7 7',
  x:        'M6 18L18 6M6 6l12 12',
  external: 'M14 3h7v7M21 3l-9 9M21 14v5a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h5',
};

// ---------------------------------------------------------------------------
// Section primitives
// ---------------------------------------------------------------------------
const Row: React.FC<{ icon: keyof typeof ICONS; children: React.ReactNode; align?: 'start' | 'center' }> = ({ icon, children, align = 'center' }) => (
  <div className={`flex gap-3 ${align === 'start' ? 'items-start' : 'items-center'}`}>
    <div className={`text-on-surface-variant day:text-gray-600 ${align === 'start' ? 'mt-0.5' : ''}`}>
      <Icon d={ICONS[icon]} />
    </div>
    <div className="flex-1 min-w-0 text-[13px] leading-snug text-on-surface day:text-gray-800">
      {children}
    </div>
  </div>
);

const SubLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-on-surface-variant day:text-gray-600 mb-1">
    {children}
  </div>
);

const Divider: React.FC = () => (
  <div className="border-t border-gray-700/40 day:border-gray-200" />
);

// ---------------------------------------------------------------------------
// Run-of-show pretty grid
// ---------------------------------------------------------------------------
const RunOfShow: React.FC<{ details: any }> = ({ details }) => {
  const slots = [
    { label: 'Load In',           value: details.load_in },
    { label: 'Sound Check',       value: details.sound_check },
    { label: 'Four Corners',      value: details.four_corners },
    { label: 'Perimeter Secured', value: details.perimeter_secured },
    { label: 'Perimeter Lifted',  value: details.perimeter_lifted },
    { label: 'Load Out',          value: details.load_out },
  ].filter(s => s.value);
  if (!slots.length) return null;
  return (
    <Row icon="tasklist" align="start">
      <SubLabel>Run of Show</SubLabel>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {slots.map(s => (
          <div key={s.label} className="flex items-baseline justify-between gap-2">
            <span className="text-[11px] text-on-surface-variant day:text-gray-600">{s.label}</span>
            <span className="text-[12px] font-semibold text-on-surface day:text-gray-900 tabular-nums">{s.value}</span>
          </div>
        ))}
      </div>
    </Row>
  );
};

// ---------------------------------------------------------------------------
// Crew section — lazy-fetched
// ---------------------------------------------------------------------------
type CrewMember = { id: number | string; firstname?: string; lastname?: string; role?: string; notes?: string };

const ROLE_TONE: Record<string, string> = {
  PIC:   'bg-amber-500/15 text-amber-300 day:text-amber-700 ring-amber-500/30 day:ring-amber-300',
  RPIC:  'bg-sky-500/15 text-sky-300 day:text-sky-700 ring-sky-500/30 day:ring-sky-300',
  VO:    'bg-purple-500/15 text-purple-300 day:text-purple-700 ring-purple-500/30 day:ring-purple-300',
  Crew:  'bg-[rgb(95_95_95/0.15)] text-on-surface-variant day:text-gray-700 ring-[rgb(95_95_95/0.3)] day:ring-gray-300',
};

const RoleBadge: React.FC<{ role?: string }> = ({ role }) => {
  if (!role) return null;
  const tone = ROLE_TONE[role] || ROLE_TONE.Crew;
  return <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ring-inset ${tone}`}>{role}</span>;
};

export const CrewSection: React.FC<{ eventId: string | number }> = ({ eventId }) => {
  const api = useApi();
  const [crew, setCrew] = useState<CrewMember[] | null>(null);
  const [errored, setErrored] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    (async () => {
      try {
        const data = await api.get(`/events/${eventId}/crew`, { silent: true });
        if (aliveRef.current) setCrew(Array.isArray(data) ? data : []);
      } catch {
        if (aliveRef.current) setErrored(true);
      }
    })();
    return () => { aliveRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // No crew or a failed fetch hides the row; Reveal keeps it mounted through its exit, so the popup closes the gap smoothly.
  const show = !errored && !(crew && crew.length === 0);

  return (
    <Reveal show={show}>
      <Row icon="users" align="start">
        <SubLabel>Crew</SubLabel>
        {crew === null ? (
          <FadeIn key="loading">
            <div className="space-y-1.5" aria-label="Loading crew">
              <div className="h-3.5 w-32 rounded bg-gray-700/40 day:bg-gray-200 animate-pulse" />
              <div className="h-3.5 w-24 rounded bg-gray-700/40 day:bg-gray-200 animate-pulse" />
            </div>
          </FadeIn>
        ) : (
          <FadeIn key="loaded">
            <div className="space-y-1">
              {crew.map(c => (
                <div key={c.id} className="flex items-center gap-2 text-[12px]">
                  <span className="text-on-surface day:text-gray-800 truncate">
                    {[c.firstname, c.lastname].filter(Boolean).join(' ') || 'Unnamed'}
                  </span>
                  <RoleBadge role={c.role} />
                </div>
              ))}
            </div>
          </FadeIn>
        )}
      </Row>
    </Reveal>
  );
};

// ---------------------------------------------------------------------------
// Body — events
// ---------------------------------------------------------------------------
export const EventBody: React.FC<{ event: AnyEvent }> = ({ event }) => {
  const details = event.details_json || {};
  const cityState = parseCityState(event.location);
  const mapHref =
    (event.launch_latitude != null && event.launch_longitude != null)
      ? `https://www.google.com/maps/search/?api=1&query=${event.launch_latitude},${event.launch_longitude}`
      : event.location
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`
        : null;

  const dateText = (() => {
    const start = formatEventDate(event.event_date, 'ddd, MMM D, YYYY');
    if (event.end_date && event.end_date !== event.event_date) {
      const end = formatEventDate(event.end_date, 'MMM D');
      return `${start} → ${end}`;
    }
    return start;
  })();

  const hasCreative = details.music || details.time_machine || details.music_link_code || details.creative;
  const showShipping = event.transport_mode === 'shipping';
  const onsiteName = details.onsite_contact_name as string | undefined;
  const onsitePhone = details.onsite_contact_phone as string | undefined;
  const contacts = Array.isArray(event.contacts) ? event.contacts.filter((c: any) => c?.name) : [];
  const hasAnyContact = !!onsiteName || contacts.length > 0;
  // Match the canonical buffered() helper in EventCalendar.js — buffer ls and
  // pyro separately then sum, so the popup's count agrees with the calendar.
  const ls = event.ls_drone_count || 0;
  const py = event.pyro_drone_count || 0;
  const bufPct = event.buffer_percentage || 0;
  const droneTotal = ls + py;
  const buffered = ls + Math.ceil(ls * bufPct / 100) + py + Math.ceil(py * bufPct / 100);

  return (
    <div className="space-y-3.5">
      {/* Time */}
      <Row icon="clock" align="start">
        <div className="font-semibold text-on-surface day:text-gray-900">{dateText}</div>
        <div className="text-[12px] text-on-surface-variant day:text-gray-600 mt-0.5 flex flex-wrap gap-x-3">
          {event.show_time && (
            <span>
              Show <span className="text-on-surface day:text-gray-800 font-medium">{event.show_time}</span>
              {event.timezone && <span className="text-on-surface-variant day:text-gray-600 ml-1">{event.timezone}</span>}
            </span>
          )}
          {event.is_double_show && event.second_show_time && (
            <span>
              2nd <span className="text-on-surface day:text-gray-800 font-medium">{event.second_show_time}</span>
            </span>
          )}
          {event.rain_date && (
            <span className="text-amber-300/80 day:text-amber-700">
              Rain {formatEventDate(event.rain_date, 'MMM D')}
            </span>
          )}
        </div>
      </Row>

      {/* Drones / Tour */}
      {(droneTotal > 0 || event.tour_id) && (
        <Row icon="ticket">
          <div className="flex items-center gap-3 flex-wrap">
            {droneTotal > 0 && (
              <span>
                <span className="text-[14px] font-bold text-primary day:text-amber-700 tabular-nums">{buffered}</span>
                <span className="text-[12px] text-on-surface-variant day:text-gray-600 ml-1">drones</span>
                {buffered !== droneTotal && (
                  <span className="text-[10px] text-on-surface-variant day:text-gray-600 ml-1">({droneTotal} + {event.buffer_percentage}% buf)</span>
                )}
              </span>
            )}
            {event.tour_id && (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-cyan-300 day:text-cyan-700">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.102 1.101" />
                </svg>
                <span className="font-semibold uppercase tracking-wider">Tour</span>
              </span>
            )}
          </div>
        </Row>
      )}

      {/* Location */}
      {event.location && (
        <Row icon="pin" align="start">
          <div className="text-[12.5px] break-words">
            {mapHref ? (
              <a href={mapHref} target="_blank" rel="noreferrer" className="hover:underline decoration-outline day:decoration-gray-400 underline-offset-2">
                {event.location}
              </a>
            ) : event.location}
          </div>
          {cityState && (
            <div className="text-[11px] text-on-surface-variant day:text-gray-600 mt-0.5">{cityState.city}, {cityState.state}</div>
          )}
        </Row>
      )}

      {/* Run of show */}
      {Object.keys(details).some(k => ['load_in','sound_check','four_corners','perimeter_secured','perimeter_lifted','load_out'].includes(k) && details[k]) && (
        <>
          <Divider />
          <RunOfShow details={details} />
        </>
      )}

      {/* Creative */}
      {hasCreative && (
        <>
          <Divider />
          <Row icon="music" align="start">
            <SubLabel>Creative</SubLabel>
            <div className="space-y-1 text-[12px]">
              {details.music && (
                <div><span className="text-on-surface-variant day:text-gray-600">Music </span><span className="text-on-surface day:text-gray-800">{details.music}</span></div>
              )}
              {details.time_machine && (
                <div><span className="text-on-surface-variant day:text-gray-600">Time Machine </span><span className="text-on-surface day:text-gray-800">{details.time_machine}</span></div>
              )}
              {details.music_link_code && (
                <div className="font-mono text-[11px]"><span className="text-on-surface-variant day:text-gray-600">Code </span><span className="text-on-surface day:text-gray-800">{details.music_link_code}</span></div>
              )}
            </div>
          </Row>
        </>
      )}

      {/* Crew (lazy) */}
      <Divider />
      <CrewSection eventId={event.id} />

      {/* Onsite + contacts */}
      {hasAnyContact && (
        <>
          <Divider />
          <Row icon="phone" align="start">
            <SubLabel>Contacts</SubLabel>
            <div className="space-y-1.5 text-[12px]">
              {onsiteName && (
                <div className="flex items-baseline gap-2">
                  <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-semibold ring-1 ring-inset bg-amber-500/15 text-amber-300 day:text-amber-700 ring-amber-500/30 day:ring-amber-300">ONSITE</span>
                  <span className="text-on-surface day:text-gray-800">{onsiteName}</span>
                  {onsitePhone && (
                    <a href={`tel:${onsitePhone}`} className="text-on-surface-variant day:text-gray-600 hover:text-primary day:hover:text-amber-700">{onsitePhone}</a>
                  )}
                </div>
              )}
              {contacts.slice(0, 3).map((c: any, idx: number) => (
                <div key={idx} className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-on-surface day:text-gray-800">{c.name}</span>
                  {c.role && <span className="text-on-surface-variant day:text-gray-600 text-[11px]">· {c.role}</span>}
                  {c.phone && <a href={`tel:${c.phone}`} className="text-on-surface-variant day:text-gray-600 hover:text-primary day:hover:text-amber-700 text-[11px]">{c.phone}</a>}
                  {c.email && <a href={`mailto:${c.email}`} className="text-on-surface-variant day:text-gray-600 hover:text-primary day:hover:text-amber-700 text-[11px] truncate">{c.email}</a>}
                </div>
              ))}
              {contacts.length > 3 && (
                <div className="text-[11px] text-on-surface-variant day:text-gray-600">+{contacts.length - 3} more</div>
              )}
            </div>
          </Row>
        </>
      )}

      {/* Transport */}
      {event.transport_mode && (
        <>
          <Divider />
          <Row icon="truck" align="start">
            {/* Both facts, not one word: an assigned truck means we drive, and
                transport_mode describes the leg somebody else carries. The
                tracking link below still keys off the mode, since freight is
                what has a tracking number. */}
            <div className="mb-1">
              <TransportChips
                hasVehicle={Array.isArray(event.vehicles) && event.vehicles.length > 0}
                transportArranged={!!event.transport_arranged}
                transportMode={event.transport_mode}
                size="compact"
              />
            </div>
            <div className="space-y-1 text-[12px]">
              {event.ship_out_date && (
                <div className="flex items-baseline gap-2">
                  <span className="text-on-surface-variant day:text-gray-600 w-12">Out</span>
                  <span className="text-on-surface day:text-gray-800">{formatEventDate(event.ship_out_date, 'ddd, MMM D')}</span>
                  {event.tracking_number_out && showShipping && (
                    <a
                      href={`https://www.fedex.com/fedextrack/?trknbr=${event.tracking_number_out}`}
                      target="_blank" rel="noreferrer"
                      className="text-on-surface-variant day:text-gray-600 hover:text-primary day:hover:text-amber-700 inline-flex items-center gap-1 text-[11px]"
                    >
                      track <Icon d={ICONS.external} className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
              {event.ship_back_date && (
                <div className="flex items-baseline gap-2">
                  <span className="text-on-surface-variant day:text-gray-600 w-12">Back</span>
                  <span className="text-on-surface day:text-gray-800">{formatEventDate(event.ship_back_date, 'ddd, MMM D')}</span>
                  {event.tracking_number_return && showShipping && (
                    <a
                      href={`https://www.fedex.com/fedextrack/?trknbr=${event.tracking_number_return}`}
                      target="_blank" rel="noreferrer"
                      className="text-on-surface-variant day:text-gray-600 hover:text-primary day:hover:text-amber-700 inline-flex items-center gap-1 text-[11px]"
                    >
                      track <Icon d={ICONS.external} className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
              {/* The travel notes a producer types on the event page. These are the
                  most-wanted lines on this popup and they were not here — the
                  calendar showed ship DATES but never what was written about them.
                  Rendered only when present so the block does not grow empty rows. */}
              {(event.transport_out_note || event.transport_back_note) && (
                <div className="pt-1 mt-1 border-t border-gray-700/40 day:border-gray-200 space-y-1">
                  {event.transport_out_note && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-on-surface-variant day:text-gray-600 w-12 shrink-0">Note out</span>
                      <span className="text-on-surface day:text-gray-800 whitespace-pre-wrap break-words">{event.transport_out_note}</span>
                    </div>
                  )}
                  {event.transport_back_note && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-on-surface-variant day:text-gray-600 w-12 shrink-0">Note back</span>
                      <span className="text-on-surface day:text-gray-800 whitespace-pre-wrap break-words">{event.transport_back_note}</span>
                    </div>
                  )}
                </div>
              )}
              {/* The truck, and the LEG notes typed on the tour timeline. A
                  DIFFERENT family from the two notes above: same subject, but a
                  different table (event_vehicle) and a different author. They were
                  rendered nowhere outside the grid that wrote them, which is the
                  whole of "none of my drive notes pop up anywhere". Labeled per
                  vehicle because a show can have a main truck and a meet-up truck. */}
              {Array.isArray(event.vehicles) && event.vehicles.length > 0 && (
                <div className="pt-1 mt-1 border-t border-gray-700/40 day:border-gray-200 space-y-1.5">
                  {event.vehicles.map((v: AnyEvent) => (
                    <div key={v.vehicle_id} className="space-y-0.5">
                      <div className="flex items-baseline gap-2">
                        <span className="text-on-surface-variant day:text-gray-600 w-12 shrink-0">Vehicle</span>
                        <span className="text-on-surface day:text-gray-900 font-medium">{v.name || v.display_code}</span>
                        {v.origin_label && (
                          <span className="text-on-surface-variant day:text-gray-600 text-[11px]">from {v.origin_label}</span>
                        )}
                      </div>
                      {([['Drive out', v.drive_out_note], ['Drive back', v.drive_back_note], ['Note', v.note]] as [string, string | null][])
                        .filter(([, text]) => !!text)
                        .map(([label, text]) => (
                          <div key={label} className="flex items-baseline gap-2 pl-14">
                            <span className="text-on-surface-variant day:text-gray-600 shrink-0 text-[11px]">{label}</span>
                            <span className="text-on-surface day:text-gray-800 whitespace-pre-wrap break-words text-[11.5px]">{text}</span>
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Row>
        </>
      )}

      {/* Notes */}
      {event.notes && (
        <>
          <Divider />
          <Row icon="notes" align="start">
            <SubLabel>Notes</SubLabel>
            <div className="text-[12.5px] text-on-surface day:text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
              {event.notes}
            </div>
          </Row>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Body — rentals / leases
// ---------------------------------------------------------------------------
const OrderBody: React.FC<{ event: AnyEvent }> = ({ event }) => {
  const isLease = !!event._isLease;
  const customer = event.customer_name_resolved || event.customer_name || '—';
  const so = event.so_number;
  const start = event.effective_date || event.event_date;
  const end = event._isPermanent ? null : (event.end_date || null);

  return (
    <div className="space-y-3.5">
      <Row icon="ticket" align="start">
        <SubLabel>{isLease ? 'Lease' : 'Rental'}</SubLabel>
        {so && (
          <div className="font-mono text-[13px] text-on-surface day:text-gray-900 tabular-nums">{so}</div>
        )}
      </Row>

      <Row icon="building">
        <span className="text-[13px] text-on-surface day:text-gray-900 font-medium truncate">{customer}</span>
      </Row>

      <Row icon="clock" align="start">
        <SubLabel>Term</SubLabel>
        <div className="text-[12.5px] text-on-surface day:text-gray-800">
          {start ? formatEventDate(start, 'ddd, MMM D, YYYY') : '—'}
        </div>
        <div className="text-[12px] text-on-surface-variant day:text-gray-600 flex items-center gap-1 mt-0.5">
          <Icon d={ICONS.arrow} className="w-3 h-3 text-on-surface-disabled day:text-gray-400" />
          {end
            ? formatEventDate(end, 'ddd, MMM D, YYYY')
            : (isLease ? <span className="text-blue-300 day:text-blue-700 font-semibold">Permanent</span> : '—')}
        </div>
      </Row>

      {event.notes && (
        <>
          <Divider />
          <Row icon="notes" align="start">
            <SubLabel>Notes</SubLabel>
            <div className="text-[12.5px] text-on-surface day:text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
              {event.notes}
            </div>
          </Row>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Header — title + status pill + optional close button
// ---------------------------------------------------------------------------
const PopupHeader: React.FC<{ event: AnyEvent; idPrefix: string; onClose: () => void; showCloseBtn?: boolean }> = ({ event, idPrefix, onClose, showCloseBtn }) => {
  const special = event._isLease ? 'lease' : event._isRental ? 'rental' : null;
  const status = special || getDisplayStatus(event);
  const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);
  const pillClass =
    special === 'rental' ? 'bg-orange-500/15 day:bg-orange-100 text-orange-300 day:text-orange-700 ring-orange-500/30 day:ring-orange-300'
    : special === 'lease' ? 'bg-blue-500/15 day:bg-blue-100 text-blue-300 day:text-blue-700 ring-blue-500/30 day:ring-blue-300'
    : (STATUS_COLORS[status] || STATUS_COLORS.pending);

  return (
    <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
      <div className="flex-1 min-w-0">
        <h3 id={idPrefix} className="text-[16px] font-bold leading-tight text-on-surface day:text-gray-900 break-words">
          {event.name || 'Untitled'}
        </h3>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize ring-1 ring-inset ${pillClass}`}>
            {statusLabel}
          </span>
          {event.creator_firstname && !special && (
            <span className="text-[11px] text-on-surface-variant day:text-gray-600">
              by {event.creator_firstname}{event.creator_lastname ? ` ${event.creator_lastname}` : ''}
            </span>
          )}
        </div>
      </div>
      {showCloseBtn && (
        <Tooltip label="Close"><IconButton label="Close" onClick={onClose} className="-mr-1.5 -mt-0.5">
          <IconClose />
        </IconButton></Tooltip>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Footer — single "Open" link
// ---------------------------------------------------------------------------
const PopupFooter: React.FC<{ event: AnyEvent; fullWidth?: boolean; onClose: () => void }> = ({ event, fullWidth, onClose }) => {
  const isOrder = !!(event._isRental || event._isLease);
  const to = isOrder ? `/orders/${event.id}` : `/events/${event.id}`;
  const label = isOrder ? 'Open order' : 'Open event page';
  return (
    <Link
      to={to}
      onClick={onClose}
      className={`${fullWidth ? 'w-full justify-center py-2.5' : 'px-3 py-1.5'} inline-flex items-center gap-1.5 rounded-md bg-primary day:bg-amber-500 text-gray-900 day:text-white text-[12.5px] font-semibold hover:brightness-110 day:hover:brightness-95 transition-all`}
    >
      {label}
      <Icon d={ICONS.arrow} className="w-3.5 h-3.5" />
    </Link>
  );
};

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
const EventClickPopup: React.FC<Props> = ({ popup, onClose }) => {
  const { event, clickX, triggerEl } = popup;
  const isMobile = useIsMobile();
  const isOrder = !!(event._isRental || event._isLease);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(triggerEl);
  triggerRef.current = triggerEl; // keep latest reference for unmount-only restore
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const accent = accentFor(event);

  // Position calculation — runs after first paint so we can measure height.
  // Centered in viewport, GUARANTEED to fit: width clamps to viewport - 2*PAD,
  // maxHeight clamps to viewport - 2*PAD. Body scrolls internally when content
  // exceeds that. Slight lean toward the click side keeps a faint sense of
  // "this came from over there" without dragging the popup off-center.
  // Position calculation. The popup's max height is enforced by CSS
  // (`maxHeight: 60vh`) so the body scrolls when content overflows — meaning
  // measurement here is already correctly capped on the very first paint
  // (no measure-then-resize jump). Vertically the popup is constrained to
  // the middle 60% of the viewport: never above 20%, never past 80%.
  useLayoutEffect(() => {
    if (isMobile) { setPos(null); return; }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const measuredH = containerRef.current?.offsetHeight ?? FALLBACK_H;

    // Vertical band: middle 60% of viewport.
    const minTop = vh * 0.2;
    const maxBottom = vh * 0.8;
    const idealTop = (vh - measuredH) / 2 - 12; // visually centered, slight upward bias
    const top = Math.max(minTop, Math.min(maxBottom - measuredH, idealTop));

    // Horizontal: anchor next to the click point — try right of click first,
    // flip to left of click if no room, clamp to viewport edge as last resort.
    const HGAP = 12;
    const idealRight = clickX + HGAP;                     // popup left edge if placed right of click
    const idealLeft = clickX - POPUP_W - HGAP;            // popup left edge if placed left of click
    let left: number;
    if (idealRight + POPUP_W <= vw - PAD) {
      left = idealRight;
    } else if (idealLeft >= PAD) {
      left = idealLeft;
    } else {
      // Viewport too narrow for either side — clamp around the click
      left = Math.max(PAD, Math.min(vw - POPUP_W - PAD, clickX - POPUP_W / 2));
    }

    setPos({ top, left });
  }, [isMobile, clickX, event.id]);

  // Open: focus the popup container once it's visibly positioned (avoids
  // focusing the off-screen first-paint placeholder).
  useEffect(() => {
    if (pos) containerRef.current?.focus();
  }, [pos]);

  // Close: restore focus to the originating bar — but only if it's still in
  // the DOM. Cleanup runs on unmount only (empty deps); we read the trigger
  // out of the ref so the latest value is used regardless of effect timing.
  useEffect(() => {
    return () => {
      const t = triggerRef.current;
      if (t && document.contains(t)) t.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ESC closes. Don't stopPropagation — other window listeners (toast
  // dismissers, future global hotkeys) deserve to see the keypress too.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Resize closes
  useEffect(() => {
    const handler = () => onClose();
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [onClose]);

  // Scroll closes — desktop only, and only when scroll happens OUTSIDE the popup.
  // The popup body has its own overflow-y-auto for tall content; scrolling that
  // would otherwise bubble up via capture-phase and instantly dismiss the popup.
  useEffect(() => {
    if (isMobile) return;
    const handler = (e: Event) => {
      const target = e.target as Node | null;
      if (target && containerRef.current?.contains(target)) return;
      onClose();
    };
    window.addEventListener('scroll', handler, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', handler, true);
  }, [isMobile, onClose]);

  const titleId = `event-popup-title-${event.id}`;
  const popupId = `event-popup-${event.id}`;
  const Body = isOrder ? OrderBody : EventBody;

  // -------------------------------------------------------------------------
  // Mobile: bottom sheet
  // -------------------------------------------------------------------------
  if (isMobile) {
    return createPortal(
      <>
        <div className="scrim z-[899] cal-popup-fade" onClick={onClose} aria-hidden />
        <div
          ref={containerRef}
          id={popupId}
          aria-labelledby={titleId}
          tabIndex={-1}
          className={`fixed left-[var(--flerpie-dock,0px)] right-0 bottom-0 transition-[left] ${DOCK_MOTION} z-[900] max-h-[88vh] flex flex-col rounded-t-2xl bg-gray-900 day:bg-white ring-1 ring-gray-700/60 day:ring-gray-200 shadow-[0_-12px_40px_rgba(0,0,0,0.5)] day:shadow-[0_-8px_30px_rgba(0,0,0,0.12)] cal-popup-sheet outline-none focus-visible:ring-2 focus-visible:ring-primary day:focus-visible:ring-amber-500`}
        >
          <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1 rounded-t-2xl overflow-hidden">
            <div className={`h-full w-full ${accent}`} />
          </div>
          <div aria-hidden="true" className="pt-3 pb-1 flex justify-center flex-shrink-0">
            <span className="block w-10 h-1 rounded-full bg-outline day:bg-gray-300" />
          </div>
          <PopupHeader event={event} idPrefix={titleId} onClose={onClose} showCloseBtn />
          <div className="px-5 pb-4 overflow-y-auto flex-1">
            <Body event={event} />
          </div>
          <div className="px-5 pt-3 pb-5 border-t border-gray-700/50 day:border-gray-200 flex-shrink-0">
            <PopupFooter event={event} fullWidth onClose={onClose} />
          </div>
        </div>
      </>,
      document.body
    );
  }

  // -------------------------------------------------------------------------
  // Desktop: anchored popup
  // -------------------------------------------------------------------------
  return createPortal(
    <>
      <div className="fixed inset-0 z-[899]" onClick={onClose} aria-hidden />
      <div
        ref={containerRef}
        id={popupId}
        aria-labelledby={titleId}
        tabIndex={-1}
        className="fixed z-[900] flex flex-col overflow-hidden rounded-xl bg-gray-900 day:bg-white ring-1 ring-gray-700/60 day:ring-gray-200 shadow-[0_18px_50px_rgba(0,0,0,0.55)] day:shadow-[0_18px_40px_rgba(15,23,42,0.18)] cal-popup-enter outline-none focus-visible:ring-2 focus-visible:ring-primary day:focus-visible:ring-amber-500"
        style={{
          top: pos?.top ?? -9999,
          left: pos?.left ?? -9999,
          width: POPUP_W,
          maxHeight: '60vh', // body scrolls beyond this; constraint applies on first render too
          opacity: pos ? 1 : 0,
        }}
      >
        {/* Status accent strip — decorative */}
        <div aria-hidden="true" className={`absolute left-0 top-0 bottom-0 w-1 ${accent}`} />
        <PopupHeader event={event} idPrefix={titleId} onClose={onClose} />
        <div className="px-5 pb-4 overflow-y-auto flex-1">
          <Body event={event} />
        </div>
        <div className="px-5 py-3 border-t border-gray-700/50 day:border-gray-200 flex justify-end flex-shrink-0">
          <PopupFooter event={event} onClose={onClose} />
        </div>
      </div>
    </>,
    document.body
  );
};

export default EventClickPopup;
